import { User } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { toast, Toaster } from 'react-hot-toast';

import { auth } from '../../shared/firebase/firebase';
import useSocket from '../../shared/hooks/socket-hook';
import { ChatMessage, ChatUI } from './ChatUI';

interface ChatWithSocketProps {
  chatInitConfig?: {
    initMessage: string;
    selectedProject?: { id: string; location?: { projectKey: string } };
    docId?: string;
    filename?: string;
  };
  chatId: string;
  taskManager?: string;
  boardId?: string;
  projectKey?: string;
  title?: string;
  history?: ChatMessage[];
}

const MAX_FILE_SIZE = 15 * 1024 * 1024;

export const ChatWithSocket: React.FC<ChatWithSocketProps> = ({
  chatId,
  chatInitConfig,
  taskManager,
  boardId,
  title,
  history,
}) => {
  const { emit, on, off, uploadFile } = useSocket(auth.currentUser!.uid);
  const [messages, setMessages] = useState<ChatMessage[]>(history || []);
  const [newMessage, setNewMessage] = useState('');
  const [typingText, setTypingText] = useState<string>();
  const [typingSender, setTypingSender] = useState<string>('');
  const [sender, setSender] = useState<User | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [docId, setDocId] = useState<string>('');

  const [updatedSelectedProject, setUpdatedSelectedProject] = useState<{ id: string } | null>(null);

  useEffect(() => {
    if (chatInitConfig) {
      const msg: ChatMessage = {
        sender: auth.currentUser?.uid || 'user',
        text: chatInitConfig.initMessage,
        context: {
          taskManager: 'jira',
          boardId: chatInitConfig?.selectedProject?.id,
          projectKey: chatInitConfig?.selectedProject?.location?.projectKey,
          // Plural `docIds` is the current contract (MVPB-54); singular `docId`/`filename` are
          // deprecated back-compat fields — send the preferred shape.
          docIds: chatInitConfig?.docId ? [chatInitConfig.docId] : undefined,
          filename: chatInitConfig.filename || (file ? file.name : undefined),
        },
        createdAt: Date.now(),
        updatedAt: Date.now(),
        chatId,
      };
      emit('chatMessage', msg);
      setMessages((prev) => [...prev, msg]);
      setNewMessage('');
      setFile(null);
      setIsFileLoading(false);
      setDocId('');
    }
  }, [chatInitConfig, chatId, emit, file]);

  useEffect(() => {
    const user = auth.currentUser;
    setSender(user);

    on('chatMessage', (message: ChatMessage) => {
      setTypingText('');
      setTypingSender('');

      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage && lastMessage.sender === message.sender && lastMessage.typing) {
          // Если последнее сообщение от того же отправителя и оно в процессе, то обновляем его
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            text: message.text,
            updatedAt: message.updatedAt,
            typing: false,
          };
          return updated;
        }
        return [...prev, message];
      });
    });

    on('typing', ({ isTyping, sender }: { isTyping: boolean; sender: string }) => {
      if (isTyping) {
        setTypingText(`${sender} is thinking...`);
        setTypingSender(sender);
        return;
      } else {
        setTypingText('');
        setTypingSender('');
      }
    });

    on('process:message', ({ message, processTitle }: { message: ChatMessage; processTitle: string }) => {
      if (message) setMessages((prev) => [...prev, message]);
      if (processTitle) {
        setTypingText(processTitle);
        setTypingSender('Agent');
      }
    });

    on('chatMetaUpdated', ({ selectedProject }: { chatId: string; selectedProject: { id: string } }) => {
      if (!selectedProject) return;
      setUpdatedSelectedProject(selectedProject);
    });

    on(
      'llmNewToken',
      ({
        type,
        token,
        runId,
        sessionId,
      }: {
        type: string;
        node: { [key: string]: any };
        sessionId: string;
        token: string;
        runId: string;
      }) => {
        if (type !== 'token') return;

        setTypingText('');
        setTypingSender('');
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          // нормализуем имя агента — используй везде одно и то же
          const AGENT = 'MFlow Agent';

          // если первое сообщение потока или последний не от агента — создаём каркас
          if (!last || ![AGENT, 'Agent'].includes(last.sender) || !last.typing) {
            updated.push({
              sender: AGENT,
              text: token ?? '',
              context: { sessionId, runId, inProcess: true },
              createdAt: Date.now(),
              updatedAt: Date.now(),
              typing: true,
              chatId,
            });
            return updated;
          }

          // иначе — аппендим токен к последнему агентскому сообщению
          updated[updated.length - 1] = {
            ...last,
            text: (last.text ?? '') + (token ?? ''),
            updatedAt: Date.now(),
            typing: true,
          };
          return updated;
        });
      },
    );

    return () => {
      off('chatMessage');
      off('typing');
      off('process:message');
      off('chatMetaUpdated');
      off('llmNewToken');
    };
  }, [chatId, on, off]);

  const handleSend = () => {
    if (!newMessage.trim()) return;

    const msg: ChatMessage = {
      sender: sender?.uid || 'user',
      text: newMessage,
      context: {
        taskManager: taskManager === 'general' ? undefined : taskManager,
        boardId,
        // Plural `docIds` is the current contract (MVPB-54); singular `docId`/`filename` are
        // deprecated back-compat fields — send the preferred shape.
        docIds: docId ? [docId] : undefined,
        filename: file ? file.name : undefined,
      },
      createdAt: Date.now(),
      updatedAt: Date.now(),
      chatId,
    };

    emit('chatMessage', msg);
    setMessages((prev) => [...prev, msg]);
    setNewMessage('');
    setFile(null);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    if (selectedFile.size > MAX_FILE_SIZE) {
      toast.error('File size exceeds 15MB limit');

      return;
    }
    setIsFileLoading(true);
    uploadFile(
      selectedFile,
      sender!.uid,
      chatId,
      (docId) => {
        toast.success('File processed successfully');
        setDocId(docId);
        setIsFileLoading(false);
      },
      (error) => {
        toast.error(error);
        setFile(null);
        setIsFileLoading(false);
      },
    );
  };

  const handleRemoveFile = () => {
    setFile(null);
    setDocId('');
    toast.success('File removed');
  };

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <ChatUI
        messages={messages}
        // Was live-updated via the now-dead `chatMetaDefined` event (BE removed its predecessor,
        // chat-definer-agent — session titling is handled elsewhere now, see the mflow knowledge
        // base "WebSocket event catalog" doc). This legacy chatId-based flow has no replacement
        // wired up yet, so the title is static for the life of this component — a real gap, not
        // a deliberate simplification; flagged as a follow-up rather than guessed at here.
        title={title || ''}
        selectedJiraBoardId={updatedSelectedProject?.id || boardId}
        projectSelectorLocked={!!updatedSelectedProject || !!boardId}
        typingText={typingText}
        typingSender={typingSender}
        newMessage={newMessage}
        onChange={setNewMessage}
        onSend={handleSend}
        file={file}
        isFileLoading={isFileLoading}
        onFileUpload={handleFileUpload}
        onRemoveFile={handleRemoveFile}
        senderId={sender?.uid}
      />
    </>
  );
};
